import { useEffect, useState } from "react"
import { X } from "lucide-react"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from "@/clientWeb/components/ui/drawer"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useAuthStore } from "@/features/auth"
import { meetingsApi, type MeetingChatHistoryMessage } from "../../services/meetings.api"

interface ChatHistoryProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Meeting uuid or readable code — the server resolves either. */
  meetingId?: string | null
}

/**
 * The group chat of a finished meeting.
 *
 * Reads the real archive written during the meeting (`chat:message` on the room
 * channel → `meeting.meeting_messages`). Group only: a private thread is
 * between two participants and is not part of the meeting's record.
 *
 * This used to render a hardcoded conversation between Varun, Priya and Naresh
 * for every meeting ever held.
 */
export default function ChatHistory({ open, onOpenChange, meetingId }: ChatHistoryProps) {
  const t = useTranslation()
  const authUser = useAuthStore((s) => s.user)
  const [messages, setMessages] = useState<MeetingChatHistoryMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)

  // Fetch on open rather than on mount: the drawer is rendered alongside the
  // meeting details, so mounting it is not a sign anyone wants to read it.
  useEffect(() => {
    if (!open || !meetingId) return
    let cancelled = false

    setLoading(true)
    setFailed(false)
    meetingsApi
      .getChatHistory(meetingId)
      .then((res) => {
        if (!cancelled) setMessages(res.messages ?? [])
      })
      .catch(() => {
        // Distinguished from "no messages" below — an empty chat and an
        // unreachable server should not look the same to the reader.
        if (!cancelled) setFailed(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, meetingId])

  const formatTime = (ts: number) =>
    new Date(ts).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent direction="right" width="w-full sm:w-[400px]">
        {/* Header */}
        <DrawerHeader className="px-6 py-4 border-b border-stroke-100 dark:border-border dark:border-slate-800 flex flex-row items-center justify-between">
          <DrawerTitle className="text-lg font-semibold text-slate-800 dark:text-white">
            {t("meetings.chatHistory")}
          </DrawerTitle>
          <DrawerClose asChild>
            <button className="w-8 h-8 rounded-full flex items-center justify-center text-primary border border-primary hover:bg-primary/10 transition-all duration-200">
              <X className="w-4 h-4" />
            </button>
          </DrawerClose>
        </DrawerHeader>

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loading && (
            <p className="text-sm text-center text-slate-500 dark:text-slate-400 py-8">
              {t("common.loading")}
            </p>
          )}

          {!loading && failed && (
            <p className="text-sm text-center text-slate-500 dark:text-slate-400 py-8">
              {t("meetings.chatHistoryUnavailable")}
            </p>
          )}

          {!loading && !failed && messages.length === 0 && (
            <p className="text-sm text-center text-slate-500 dark:text-slate-400 py-8">
              {t("meetings.noChatInMeeting")}
            </p>
          )}

          {!loading &&
            !failed &&
            messages.map((msg) => {
              // The archived sender id is the peerId, which for a signed-in
              // participant is their user id.
              const isMe = !!authUser?.id && msg.from === authUser.id
              return (
                <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                      isMe
                        ? "bg-primary text-white rounded-br-md"
                        : "bg-slate-100 dark:bg-card text-slate-800 dark:text-slate-200 rounded-bl-md"
                    }`}
                  >
                    {!isMe && (
                      <p className="text-xs font-semibold text-primary mb-1">
                        {msg.fromName || t("panels.unknown")}
                      </p>
                    )}
                    {msg.attachment ? (
                      <a
                        href={msg.attachment.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm leading-relaxed underline break-all"
                      >
                        {msg.attachment.name}
                      </a>
                    ) : (
                      <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                        {msg.text}
                      </p>
                    )}
                    <p
                      className={`text-[10px] mt-1 ${
                        isMe ? "text-white/70" : "text-slate-500 dark:text-slate-400"
                      }`}
                    >
                      {formatTime(msg.ts)}
                    </p>
                  </div>
                </div>
              )
            })}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
